import { installStaleBuildReload } from './staleBuildReload';
import { startReleaseSighting } from './releaseSighting';
import { sessionOpening, toResult } from '@/infrastructure/api/v1/client';
import { v1App } from '@/infrastructure/clients/BackendClient';
import { runtimeEnv } from '@/lib/runtimeEnv';
declare const __APP_COMMIT__: string;
// The app starts: for a signed-in user, the user, org, environment, remote config and version are one read (getSessionBootstrap)
sessionOpening();
// A tab left open across a deploy reloads once for the new build when one of its code files is gone
installStaleBuildReload();
startReleaseSighting({ platform: 'frontend', version: __APP_VERSION__, build: __APP_BUILD__, sha: __APP_COMMIT__ }, {
  report: async (body, signal) => (await toResult(v1App.POST('/api/v1/client-sightings', { body, signal }))).data,
  environment: async signal => (await toResult(v1App.GET('/api/v1/version', { signal }))).data?.environment ?? null,
});
//path: src\main.tsx

import './polyfills';
import * as Sentry from "@sentry/react";
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
// The default themes' tokens, so the page paints before the app applies the chosen theme
import './modules/theme/theme-defaults.css';
import { startThemes } from './modules/theme';
import { onEditorChange } from '@/modules/undoRedo';
import { MATERIAL_ROWS_CHANGED } from './modules/materialRegistry/api';
import './index.css';
import { ConsentProvider } from './modules/cookieConsent';
import { ConditionalPostHogProvider } from './modules/cookieConsent';
import { captureFirstTouchAttribution } from './modules/attribution/attribution';
import { capturePendingTemplateLink } from './modules/templateScenes/pendingTemplateLink';
import { getDeployEnvironment } from './lib/deployEnvironment';

startThemes();

// The window events older parts of the app listen for, from what undo and the scene's changes from elsewhere announce
onEditorChange(change => {
  if (change.kind === 'library') window.dispatchEvent(new CustomEvent('libraryRefetchNeeded'));
  else if (change.kind === 'anchors') window.dispatchEvent(new CustomEvent('sceneAnchorChanged'));
  else if (change.kind === 'screenMessageStyle') window.dispatchEvent(new CustomEvent('screenUILibraryRefetchNeeded', { detail: { typeId: change.typeId } }));
  else if (change.kind === 'materials') window.dispatchEvent(new CustomEvent(MATERIAL_ROWS_CHANGED, { detail: { assetInstanceId: change.assetInstanceId } }));
});

// Capture first-touch campaign UTM before React Router can strip the query string.
captureFirstTouchAttribution();
capturePendingTemplateLink();

// App version + build number from vite.config.ts (reads from package.json)
declare const __APP_VERSION__: string;
declare const __APP_BUILD__: string;

// Check if we're in development (localhost)
const isDevelopment = import.meta.env.MODE === 'development' ||
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

const sentryEnvironment = isDevelopment ? 'development' : getDeployEnvironment();

// Get Sentry DSN from environment variable
const sentryDsn = (runtimeEnv.VITE_SENTRY_DSN || process.env.VITE_SENTRY_DSN) as string;

// Only initialize Sentry if not in development mode
if (!isDevelopment) {
  if (!sentryDsn) {
    throw new Error("Sentry DSN not found. Make sure VITE_SENTRY_DSN is set in your .env file.");
  }

  Sentry.init({
    dsn: sentryDsn,
    sendDefaultPii: true,
    enableLogs: true,
    
    // Release tracking - links errors to specific versions
    release: `lureo-frontend@${__APP_VERSION__}+${__APP_BUILD__}`,
    environment: sentryEnvironment,
    
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["error"] }),
    ],
    
    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    tracePropagationTargets: ["localhost", /^https:\/\/lureo\.app/, /^https:\/\/create\.lureo\.com/, /^https:\/\/create\.staging\.lureo\.com/],

    // CPU Profiling for performance analysis
    profilesSampleRate: 0.1, // 10% of transactions will have profiling
    profileLifecycle: "trace",

  });
} else {
  console.log('🔧 Sentry disabled in development mode');
}

const appContent = isDevelopment ? (
  <App />
) : (
  <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
    <App />
  </Sentry.ErrorBoundary>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ConsentProvider>
      <ConditionalPostHogProvider>
        {appContent}
      </ConditionalPostHogProvider>
    </ConsentProvider>
  </React.StrictMode>,
);
